import cartApi from '@/api/cart';
import { loadEntity, loadPaginatedEntity, enhanceOrAdd } from '@/helpers/utility';

const state = {
    byId: {},
    currentPage: null,
    totalPages: null,
    currentPageIds: null,

    statuses: {
        pending: 'Pending',
        evaluated: 'Evaluated',
        complete: 'Complete',
        canceled: 'Canceled',
    },

    giveBackContainersOptions: {
        yes: 'Yes',
        no: 'No',
        other: 'Other',
    },

    tradeTypes: {
        merchandise_credit: 'Merch. Credit',
        straight_swap: 'Straight Swap',
        swap_and_cp: 'Swap + Cash Payout',
        cp: 'Cash Payout',
    },

    itemsWeDontBuyActions: {
        donate: 'Donate',
        return: 'Return',
        ask: 'Ask',
    },
};

const getters = {
    currentPageCarts(state) {
        if (state.currentPageIds)
            return state.currentPageIds.map(id => state.byId[id]);
    },
};

const actions = {
    loadCart({ state, dispatch, commit }, { cart, isNew }) {
        loadEntity({ cart });

        // If we're on the first page and we're adding a new cart, add it to the top of the page
        if (isNew && state.currentPage === 1)
            commit('ADD_CART_TO_CURRENT_PAGE', { cart });
    },
    async getPaginatedCarts({ commit }, { page, status, search, completedOn }) {
        let pagedCarts = await cartApi.paginatedList({ page, status, search, completedOn });
        loadPaginatedEntity('carts', pagedCarts);
    },
    async getCarts(store, { status, search, customer }) {
        let cartResponse = await cartApi.list({ status, search, customer });
        let carts = cartResponse.data;
        loadEntity({ carts });
        return carts;
    },
    async createCart({ dispatch }, { cart }) {
        cart = await cartApi.create(cart);
        loadEntity({ cart });
        return cart;
    },
    async updateCart({ dispatch }, { cart }) {
        cart = await cartApi.update(cart);
        loadEntity({ cart });
        return cart;
    },
    async roundUpTotalCart({ dispatch }, { cart }) {
        cart = await cartApi.roundUpTotal(cart);
        loadEntity({ cart });
        return cart;
    },
    async findCart({ dispatch }, { id, code }) {
        let cart = null;
        if (id)
            cart = await cartApi.get(id);
        else
            cart = await cartApi.find(code);
        loadEntity({ cart });
        return cart;
    },
    async resetCart({ dispatch }, { cart }) {
        cart = await cartApi.reset(cart);
        loadEntity({ cart });
        return cart;
    },
    async evaluateCart({ dispatch }, { cart }) {
        cart = await cartApi.evaluate(cart);
        loadEntity({ cart });
        return cart;
    },
    async acceptCart({ dispatch }, { cart }) {
        cart = await cartApi.accept(cart);
        loadEntity({ cart });
        return cart;
    },
    async declineCart({ dispatch }, { cart }) {
        cart = await cartApi.decline(cart);
        loadEntity({ cart });
        return cart;
    },
    async completeCart({ dispatch }, { cart }) {
        cart = await cartApi.complete(cart);
        loadEntity({ cart });
        return cart;
    },
    async cancelCart({ dispatch }, { cart }) {
        cart = await cartApi.cancel(cart);
        loadEntity({ cart });
        return cart;
    },
    subscribe({ dispatch, commit }, { channels }) {
        if (channels.indexOf('carts') === -1)
            return;

        Echo.leave('carts');

        Echo.private('carts')
            .listen('Carts.CartUpdated', (e) => dispatch('loadCart', { cart: e.cart, isNew: e.isNew }))
            .listen('Carts.CartItemUpdated', (e) => loadEntity({ cartItem: e.cartItem }))
            .listen('Carts.CartItemDeleted', (e) => {
                commit('REMOVE_CARTITEM', { cartItem: { id: e.id } });
                loadEntity({ cart: e.cart });
            });
    },
    unsubscribe(store, { channels }) {
        if (channels.indexOf('carts') === -1)
            return;

        Echo.leave('carts');
    },
};

const mutations = {
    LOAD_ENTITIES(state, { carts, cartItems, meta }) {
        if (carts)
            Object.values(carts).forEach(cart => enhanceOrAdd(state.byId, cart.id, cart));

        if (cartItems) {
            Object.values(cartItems).forEach(cartItem => {
                let cart = state.byId[cartItem.cart_id];
                if (cart && cart.cart_items) {
                    if (cart.cart_items.indexOf(cartItem.id) === -1)
                        cart.cart_items.push(cartItem.id);
                }
            });
        }

        if (meta && meta.type === 'carts' && meta.paginated) {
            state.currentPage = meta.currentPage;
            state.totalPages = meta.totalPages;
            state.currentPageIds = meta.ids;
        }
    },
    ADD_CART_TO_CURRENT_PAGE(state, { cart }) {
        if (state.currentPageIds)
            state.currentPageIds.unshift(cart.id);
    },
    UPDATE_CART(state, { cart }) {
        enhanceOrAdd(state.byId, cart.id, cart)
    },
    ADD_CARTITEM_TO_CART(state, { cart, cartItem }) {
        state.byId[cart.id].cart_items.push(cartItem.id);
    },
    REMOVE_CARTITEM(state, { cartItem }) {
        _.forEach(state.byId, (cart) => {
            _.pull(cart.cart_items, cartItem.id);
        });
    },
};

export default {
    state,
    getters,
    actions,
    mutations,
};
